/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  CommonFlowState,
  ListActionPipelines,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  ChecklistExportToOfficeDto,
  ChecklistImportFromMasterZoneDto,
  ChecklistStatus,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import { AppRouter, getRouteInitData } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$, map$, switchMap$ } from '@gh/rx/operators';
import { EMPTY } from 'rxjs';

import { MasterZone } from '../../../offices/resources/master-zone.model';
import { ChecklistsBusinessService } from '../../resources';
import { ChecklistItem } from '../../resources/checklists.model';
import {
  ChecklistsExportToOfficeDialogComponent,
  ChecklistsExportToOfficeDialogConfig,
  ChecklistsExportToOfficeDialogResult,
} from '../checklists-export-to-office-dialog/checklists-export-to-office-dialog.component';

@Component({
  standalone: false,
             selector: 'gh-checklists-list',
             templateUrl: 'checklists-list.component.html',
             styleUrls: ['checklists-list.component.scss'],
           })
export class ChecklistsListComponent implements OnInit {

  checklist: ChecklistItem[];
  showDeleted: boolean = false;
  masterZones: MasterZone[] = [];

  get isCorpOrMasterView(): boolean {
    return this.context.masterView || this.context.corporateView;
  }

  get isCorpOrMaster(): boolean {
    return this.context.hasSystemRole(SystemRoleBit.Master)
    || this.context.hasSystemRole(SystemRoleBit.Corporate);
  }

  get hasEditPermission(): boolean {
    return this.isCorpOrMaster
      || this.context.hasPermission(BuilderPermission.Setup.Checklists.EDIT)
      || this.context.hasPermission(BuilderPermission.Setup.Checklists.VIEW);
  }

  get corporateView(): boolean {
    return this.context.corporateView;
  }

  get isOfficeView(): boolean {
    return this.context.officeView;
  }

  get canExportToOffice(): boolean {
    return this.isCorpOrMasterView;
  }

  get canImportFromMasterZone(): boolean {
    return this.isOfficeView
      && this.context.hasPermission(BuilderPermission.Setup.Checklists.IMPORT);
  }

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private context: BuilderUserContext,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              private dialogService: DialogService,
              private navigationService: BuilderNavigationService,
              private commonActions: CommonActions,
              private pipelines: ListActionPipelines,
              private checklistBusiness: ChecklistsBusinessService) {
  }

  ngOnInit(): void {
    const { checklist, masterZones } = getRouteInitData(this.route);
    this.checklist = checklist;
    this.masterZones = masterZones || [];
  }

  onOpenExportToOfficeDialog(): void {
    if (!this.canExportToOffice) {
      return;
    }
    this.dialogService.openWithInput(
      ChecklistsExportToOfficeDialogComponent,
      <ChecklistsExportToOfficeDialogConfig>{
        masterZones: this.masterZones,
        corporateView: this.corporateView,
      },
    ).afterClosed().pipe(
      filter$((result): result is ChecklistsExportToOfficeDialogResult => !!result?.office?.officeId),
    ).subscribe((result) => {
      this.exportToOffice(result);
    });
  }

  onImportFromMasterZone(): void {
    if (!this.canImportFromMasterZone) {
      return;
    }
    const officeId = this.context.office!.id;
    const officeName = this.context.office!.name;

    this.checklistBusiness.getImportAvailability(officeId).pipe(
      this.waitingService.operator(),
      switchMap$((availability) => {
        if (availability.masterZoneChecklistCount > 0) {
          return this.dialogService.confirmation(yesNoConfirmation(
            'msg_import_checklists_from_master_zone',
            { officeName },
          )).pipe(
            filter$(yesAnswer),
            map$(() => <ChecklistImportFromMasterZoneDto>{ officeId, importFromCorporate: false }),
          );
        }
        if (availability.corporateChecklistCount > 0) {
          return this.dialogService.confirmation(yesNoConfirmation(
            'msg_import_checklists_from_corporate',
            { officeName },
          )).pipe(
            filter$(yesAnswer),
            map$(() => <ChecklistImportFromMasterZoneDto>{ officeId, importFromCorporate: true }),
          );
        }
        this.notificationService.warning('msg_import_checklists_nothing_available');
        return EMPTY;
      }),
      switchMap$((payload) => this.checklistBusiness.importFromMasterZone(payload).pipe(
        this.waitingService.operator(),
        map$(() => payload),
      )),
    ).subscribe(
      (payload) => {
        const successMessageId = payload.importFromCorporate
          ? 'msg_import_checklists_from_corporate_success'
          : 'msg_import_checklists_from_master_zone_success';
        this.notificationService.success(successMessageId, { officeName });
        this.reload();
      },
      (error) => this.notificationService.processAll(error),
    );
  }

  onDropCheckItem(event: CdkDragDrop<ChecklistItem>): void {
    moveItemInArray(this.checklist, event.previousIndex, event.currentIndex);
    this.checklistBusiness.updateOrder(this.checklist).pipe(
      (this.waitingService.operator()))
      .subscribe(
        (checklist: ChecklistItem[]) => {
          this.checklist = checklist;
        },
        (error) => this.notificationService.processAll(error),
      );
  }

  onDelete(template: ChecklistItem): void {
    const flow = this.pipelines.checkUsageToggleItem({
       messages: {
         confirmUsed: {
           id: this.isCorpOrMasterView ? 'msg_checklists_mark_deleted' : 'msg_office_checklist_mark_deleted',
           params: { name: template.name },
         },
         confirmNotUsed: {
           id: 'msg_office_checklist_delete',
           params: { name: template.name },
         },
       },
       executes: {
         checkUsage: this.commonActions.execute((state: CommonFlowState<any>) =>
                                                  this.checklistBusiness.checkUsage(state.value)),
         summary: this.commonActions.execute((state: CommonFlowState<any>) =>
                                               this.checklistBusiness.deactivateOrDelete(template, state.value)),
       },
       finish: () => {
         this.showDeleted = true;
         this.reload();
       },
      });

    flow.run({
       value: template,
     });
  }

  onUndelete(checklistItem: ChecklistItem): void {
    this.dialogService.confirmation(yesNoConfirmation(this.isCorpOrMasterView ? 'msg_checklists_undelete' :
                                                        'msg_office_checklist_undelete')).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.checklistBusiness.undelete(checklistItem.id).pipe((this.waitingService.operator<ChecklistItem>()))))
      .subscribe(() => {
        this.reload();
      },         this.notificationService.processAll);
  }

  onCreate(): void {
    this.router.navigateToChild(['/setup/checklists/new']);
  }

  onEdit(checklistItem: ChecklistItem): void {
    if (!this.hasEditPermission) {
      return;
    }
    this.router.navigateToChild([`/setup/checklists/${checklistItem.id}`]);
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
                                     label: label ? label : 'lbl_mobile_app_checklists',
                                     entityType: entityType ? entityType : 'CHECKLIST',
                                     entityId: entityId ? entityId : undefined,
                                   });
  }

  reload(): void {
    this.checklistBusiness.findAll(this.showDeleted).subscribe((data) => {
      this.checklist = data;
    });
  }

  isActive(item: ChecklistItem): boolean {
    return item.status === ChecklistStatus.ACTIVE;
  }

  canBeDeleted(item: ChecklistItem): boolean {
    if (item.status !== ChecklistStatus.ACTIVE) {
      return false;
    }
    if (this.context.corporateView) {
      return true;
    } else if (this.context.masterView) {
        return !item.parentId && !item.notEditable ;
    } else {
      return !item.parentId && !item.notEditable &&
        (this.isCorpOrMaster || this.context.hasPermission(BuilderPermission.Setup.Checklists.DELETE));
    }
  }

  canBeUnDeleted(item: ChecklistItem): boolean {
    if (item.status === ChecklistStatus.ACTIVE) {
      return false;
    }
    if (this.context.corporateView) {
      return true;
    } else if (this.context.masterView) {
      return !item.notEditable && !item.parentId;
    } else {
      return !item.parentId && !item.notEditable
        && (this.isCorpOrMaster || this.context.hasPermission(BuilderPermission.Setup.Checklists.UNDELETE));
    }
  }

  private exportToOffice(result: ChecklistsExportToOfficeDialogResult): void {
    if (!this.canExportToOffice) {
      return;
    }
    const { office, masterZoneId, masterZoneName } = result;
    const resolvedMasterZoneId = masterZoneId ?? this.context.masterZone?.id;
    if (!resolvedMasterZoneId) {
      return;
    }

    this.checklistBusiness.getExportAvailability(resolvedMasterZoneId).pipe(
      this.waitingService.operator(),
      switchMap$((availability) => {
        const buildPayload = (importFromCorporate: boolean): ChecklistExportToOfficeDto => ({
          officeId: office.officeId,
          masterZoneId: resolvedMasterZoneId,
          masterZoneName,
          importFromCorporate,
        });

        if (availability.masterZoneChecklistCount > 0) {
          return this.dialogService.confirmation(yesNoConfirmation(
            'msg_export_checklists_to_office',
            { officeName: office.name },
          )).pipe(
            filter$(yesAnswer),
            map$(() => buildPayload(false)),
          );
        }
        if (availability.corporateChecklistCount > 0) {
          return this.dialogService.confirmation(yesNoConfirmation(
            'msg_export_checklists_from_corporate',
            { officeName: office.name },
          )).pipe(
            filter$(yesAnswer),
            map$(() => buildPayload(true)),
          );
        }
        this.notificationService.warning('msg_export_checklists_nothing_available');
        return EMPTY;
      }),
      switchMap$((payload) => this.checklistBusiness.exportToOffice(payload).pipe(
        this.waitingService.operator(),
        map$(() => payload),
      )),
    ).subscribe(
      (payload) => {
        const successMessageId = payload.importFromCorporate
          ? 'msg_export_checklists_from_corporate_success'
          : 'msg_export_checklists_to_office_success';
        this.notificationService.success(successMessageId, { officeName: office.name });
      },
      (error) => this.notificationService.processAll(error),
    );
  }

}
